import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarCheck, Clock, Video } from "lucide-react";
import SkylineDivider from "./SkylineDivider";

const LINKEDIN_URL = "https://www.linkedin.com/company/huddleup-sa/";
const RIYADH_TZ = "Asia/Riyadh";

const fmt = (date, timeZone, opts) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: timeZone || undefined, ...opts }).format(date);

/**
 * Branded thank-you shown the moment a Cal.com booking succeeds.
 *
 * Props:
 *   booking — { start, duration, title, name, email, timeZone } from CalEmbed
 *   product — recommended product from the diagnostic (optional)
 */
export default function BookingConfirmation({ booking, product }) {
  const start = booking?.start ? new Date(booking.start) : null;
  const valid = start && !Number.isNaN(start.getTime());
  const localTz = booking?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const firstName = (booking?.name || "").trim().split(/\s+/)[0];

  const dayLine = valid ? fmt(start, localTz, { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : null;
  const localTime = valid ? fmt(start, localTz, { hour: "numeric", minute: "2-digit" }) : null;
  const riyadhTime = valid ? fmt(start, RIYADH_TZ, { hour: "numeric", minute: "2-digit" }) : null;
  const showRiyadh = valid && localTz !== RIYADH_TZ;

  return (
    <section
      data-testid="booking-confirmation"
      className="kingdom-fade text-paper py-24 md:py-32 min-h-[80vh] relative overflow-hidden"
    >
      <div className="skyline"></div>
      <div className="skyline-sunrise"></div>
      <div className="ambient-glow"></div>
      <div className="max-w-[1000px] mx-auto px-5 md:px-10 relative z-10 diag-step-enter">
        <span className="eyebrow">You’re booked</span>
        <h2 className="font-display text-4xl md:text-6xl leading-[1.05] mt-6">
          See you soon{firstName ? `, ${firstName}` : ""}. <br />
          <span className="italic text-gold">The Kingdom is on the calendar.</span>
        </h2>
        <p className="mt-6 text-base md:text-lg text-warm/80 leading-relaxed max-w-2xl font-light">
          {product && product !== "We need more information"
            ? `We will walk through ${product} as your likely first move, and what it would take to make it real.`
            : "We will walk through where you are, whether the Kingdom makes sense right now, and the right first move."}
        </p>

        <div
          data-testid="booking-details-card"
          className="mt-12 grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-px bg-gold/20 border border-gold/20 max-w-3xl"
        >
          <div className="bg-obsidian/70 p-8 md:p-10">
            <div className="flex items-center gap-3 text-gold">
              <CalendarCheck size={18} />
              <span className="text-[11px] uppercase tracking-[0.24em]">{booking?.title || "HuddleUp Intro Call"}</span>
            </div>
            {valid ? (
              <>
                <div data-testid="booking-date" className="font-display text-2xl md:text-3xl mt-6 leading-tight">
                  {dayLine}
                </div>
                <div data-testid="booking-time" className="font-display text-xl md:text-2xl mt-2 text-gold">
                  {localTime}
                  <span className="text-warm/50 text-sm font-sans ml-3 tracking-wide">{localTz.replace(/_/g, " ")}</span>
                </div>
                {showRiyadh && (
                  <div data-testid="booking-time-riyadh" className="mt-2 text-sm text-warm/60">
                    {riyadhTime} in Riyadh
                  </div>
                )}
              </>
            ) : (
              <div data-testid="booking-date" className="font-display text-2xl mt-6">
                Your time is confirmed.
              </div>
            )}
          </div>
          <div className="bg-obsidian/50 p-8 md:p-10 flex flex-col justify-between gap-8">
            <div className="space-y-4 text-sm text-warm/75">
              <div className="flex items-center gap-3">
                <Clock size={15} className="text-gold shrink-0" />
                <span>{booking?.duration || 30} minutes</span>
              </div>
              <div className="flex items-center gap-3">
                <Video size={15} className="text-gold shrink-0" />
                <span>Video call · link in your invite</span>
              </div>
            </div>
            {booking?.email && (
              <p data-testid="booking-invite-email" className="text-xs text-warm/50 leading-relaxed">
                Calendar invite sent to <span className="text-warm/80">{booking.email}</span>.
              </p>
            )}
          </div>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row sm:items-center gap-6">
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="booking-linkedin-follow"
            className="btn-gold"
          >
            Follow HuddleUp on LinkedIn
            <ArrowUpRight size={16} />
          </a>
          <Link
            to="/market-notes"
            data-testid="booking-market-notes-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-warm/60 hover:text-gold transition-colors duration-500"
          >
            Read our Market Notes while you wait
            <ArrowRight size={12} />
          </Link>
        </div>
      </div>
      <SkylineDivider variant="gold" opacity={0.22} height={160} className="skyline-bottom" />
    </section>
  );
}
